import { useMemo } from 'react'
import { ContainerTask, SortField, SortDirection } from '../types/container'
import { statusConfig, taskTypeConfig } from '../data/dispatchData'
import { parseLfd } from '../utils/lfdDate'
import {
  GATE_OUT_STATUSES,
  compareByGateOut,
  compareByCompletedDay,
  FIRST_STOP_APPT_STATUSES,
  compareByFirstStopAppt,
  ORDER_CREATED_STATUSES,
  compareByOrderCreated,
} from '../utils/apptSchedule'

interface UseTaskDataOptions {
  data: ContainerTask[]
  searchTerm?: string
  selectedDriver?: string
  selectedShipper?: string
  selectedOrderingParty?: string
  selectedStatus?: string
  lfdFilter?: string
  customLfdDate?: string
  importExportFilter?: string
  destinationFilter?: string
  portFilter?: string
  showMarkedOnly?: boolean
  markedTaskIds?: Set<string>
  sortField?: SortField | null
  sortDirection?: SortDirection
  completedTimeFilter?: string
  customCompletedDate?: string
}

/**
 * On Hold 列表分成三段，按「能不能马上动手」从上到下排。
 *
 *   1. Awaiting Gate Fee — 柜子在场、其它全清，只差一笔码头费。调度员付掉当场
 *      就能提，是这三类里唯一立刻能动的。
 *   2. Transfer for Exam — 海关把柜子拉去查验了，人已经不在码头。催谁都没用，
 *      只能等它回来。
 *   3. Other On Hold     — 其余的：还欠别的费、船司没放、海关没清……
 *
 * 混在一张表里看，能干活的那些会被淹没 —— 分段就是为了让第一段一眼可见。
 *
 * 导出成独立函数，是因为板子有两条渲染管道，而且拿到结果之后各自还会再排一次；
 * 谁排在最后一步谁说了算。上线时就是靠只在一条管道里分组翻的车。
 */
export type OnHoldSection = 'gate-fee' | 'closed-area' | 'exam' | 'other'

/**
 * 四段的先后：
 *   gate-fee    付掉这一笔柜子当场就能提 —— 唯一立刻能解决的
 *   exam        海关拉走查验
 *   closed-area 码头把柜子关起来了
 *   other       其余
 *
 * exam 在 closed-area 前面是业务定的（2026-09-10）。
 *
 * 同时命中时也按这个顺序取第一个 —— 实际上两者互斥（查验意味着柜子已经出了
 * 码头，封闭区意味着它还在场内），所以这个先后只影响显示分段。
 */
export const ON_HOLD_SECTIONS: OnHoldSection[] = ['gate-fee', 'exam', 'closed-area', 'other']

export function onHoldSectionOf(t: ContainerTask): OnHoldSection {
  if (t.awaitingGateFee) return 'gate-fee'
  if (t.closedArea) return 'closed-area'
  if (t.customsExam) return 'exam'
  return 'other'
}

/**
 * 必须是排序链的最后一步，且要在分页之前 —— 否则翻页会把同一段拆散。
 *
 * @param onHoldOnly 只有当前筛的就是 On Hold 时才分段。别的视图状态本来就是
 *   混的，把某一类挑到最上面只会让顺序变得没法解释。
 * @param keepPairsAdjacent 配对相邻的处理，在每一段里各跑一次 —— 放在分段
 *   之前的话，先配好的一对会被分段从中间劈开。
 */
export function groupOnHoldSections(
  list: ContainerTask[],
  onHoldOnly: boolean,
  keepPairsAdjacent: (l: ContainerTask[]) => ContainerTask[] = (l) => l
): ContainerTask[] {
  if (!onHoldOnly) return keepPairsAdjacent(list)
  const bySection = ON_HOLD_SECTIONS.map((sec) => list.filter((t) => onHoldSectionOf(t) === sec))
  // 只有一段非空 = 根本没分出段来，顺序一个字都不动。
  if (bySection.filter((g) => g.length > 0).length < 2) return keepPairsAdjacent(list)
  return bySection.flatMap((g) => (g.length ? keepPairsAdjacent(g) : []))
}

/**
 * 某个状态筛选默认怎么排，没有就返回 null（保持接口给的顺序）。
 *
 * 两条各有各的方向，因为两列回答的问题不一样：
 *
 *   Loading / Empty  出闸最早的在前 —— 柜子还在外面，per-diem 一直在跑，出来
 *                    最久的最烧钱，就是最该先动的那只。
 *   Completed        完成最晚的在前 —— 活已经干完了，没什么可催的，最近干完的
 *                    才是还要对账、还要回头看的那些。
 *   Unassigned       第一站的约最早的在前 —— 还没派人，看的是「接下来先安排谁」，
 *                    司机第一个要卡的时间点就是排序依据。日期过了的不下沉，
 *                    靠 Route 列里那个时间标红说话。
 *   On Vessel        建单最早的在前 —— 柜子还在船上，码头那边一个日期都还没有，
 *                    能说明「这单压了多久」的只剩它被建出来的那天，等得最久的
 *                    该排在最上面。
 */
function defaultSorterFor(
  selectedStatus?: string
): ((a: ContainerTask, b: ContainerTask) => number) | null {
  if (!selectedStatus) return null
  if (GATE_OUT_STATUSES.has(selectedStatus)) return compareByGateOut
  if (selectedStatus === 'completed') return compareByCompletedDay
  if (FIRST_STOP_APPT_STATUSES.has(selectedStatus)) return compareByFirstStopAppt
  if (ORDER_CREATED_STATUSES.has(selectedStatus)) return compareByOrderCreated
  return null
}

export const useTaskData = (options: UseTaskDataOptions) => {
  const {
    data,
    searchTerm,
    selectedDriver,
    selectedShipper,
    selectedOrderingParty,
    selectedStatus,
    lfdFilter,
    customLfdDate,
    importExportFilter,
    destinationFilter,
    portFilter,
    showMarkedOnly,
    markedTaskIds,
    sortField,
    sortDirection,
    completedTimeFilter,
    customCompletedDate,
  } = options

  // LFD urgency calculation
  const getLFDUrgency = (lfdDate: string): string => {
    if (!lfdDate || lfdDate === "Pending") return "normal"

    // Local midnight of the LFD day. Comparing the raw ISO instant instead put
    // a 9/1 free day into the 8/31 bucket for any viewer west of UTC.
    const lfd = parseLfd(lfdDate)
    if (!lfd) return "normal"
    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const tomorrow = new Date(today)
    tomorrow.setDate(tomorrow.getDate() + 1)

    if (lfd < today) return "overdue"
    if (lfd.toDateString() === today.toDateString()) return "today"
    if (lfd.toDateString() === tomorrow.toDateString()) return "tomorrow"
    if (lfd < new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000)) return "this-week"

    return "normal"
  }

  // LFD styling based on urgency
  const getLFDStyles = (urgency: string): string => {
    switch (urgency) {
      case "overdue":
        return "bg-red-100 text-red-800 border border-red-200"
      case "today":
        return "bg-orange-100 text-orange-800 border border-orange-200"
      case "tomorrow":
        return "bg-yellow-100 text-yellow-800 border border-yellow-200"
      case "this-week":
        return "bg-blue-100 text-blue-800 border border-blue-200"
      default:
        return "bg-slate-100 text-slate-700 border border-slate-200"
    }
  }

  // Date formatting function
  const formatDateTime = (dateString: string): string => {
    if (!dateString || dateString === "Pending") return "Pending"

    try {
      const date = new Date(dateString)
      const now = new Date()
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
      const tomorrow = new Date(today)
      tomorrow.setDate(tomorrow.getDate() + 1)

      if (date.toDateString() === today.toDateString()) {
        return `Today ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`
      } else if (date.toDateString() === tomorrow.toDateString()) {
        return `Tomorrow ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`
      } else {
        return date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      }
    } catch {
      return dateString
    }
  }

  // Check if date matches LFD filter
  const matchesLFDFilter = (lfdDate: string, filter: string, customDate: string): boolean => {
    if (!filter || filter === "all") return true
    if (!lfdDate || lfdDate === "Pending") return false

    const lfd = parseLfd(lfdDate)
    if (!lfd) return false
    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const tomorrow = new Date(today)
    tomorrow.setDate(tomorrow.getDate() + 1)
    const weekFromNow = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000)

    switch (filter) {
      case "today":
        return lfd.toDateString() === today.toDateString()
      case "tomorrow":
        return lfd.toDateString() === tomorrow.toDateString()
      case "this-week":
        return lfd >= today && lfd < weekFromNow
      case "overdue":
        return lfd < today
      case "custom": {
        // The picker hands over "YYYY-MM-DD", which new Date() reads as UTC
        // midnight — i.e. the day before, here.
        const customDateObj = parseLfd(customDate)
        if (!customDateObj) return false
        return lfd.toDateString() === customDateObj.toDateString()
      }
      default:
        return true
    }
  }

  // Check if a completed/cancelled task falls within the selected time range
  const matchesCompletedTimeFilter = (item: ContainerTask): boolean => {
    const terminalStatuses = ['completed', 'cancelled', 'uncompleted']
    if (!terminalStatuses.includes(item.status)) return true
    if (!selectedStatus || !terminalStatuses.includes(selectedStatus)) return true
    if (!completedTimeFilter || completedTimeFilter === 'all') return true

    const lastUpdate = item.lastUpdate
    if (!lastUpdate) return false

    const updateDate = new Date(lastUpdate)
    if (isNaN(updateDate.getTime())) return false

    const now = new Date()
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate())

    switch (completedTimeFilter) {
      case 'today':
        return updateDate >= todayStart
      case '7days': {
        const sevenDaysAgo = new Date(todayStart)
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7)
        return updateDate >= sevenDaysAgo
      }
      case '30days': {
        const thirtyDaysAgo = new Date(todayStart)
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)
        return updateDate >= thirtyDaysAgo
      }
      case 'custom': {
        if (!customCompletedDate) return false
        const customDate = new Date(customCompletedDate)
        const nextDay = new Date(customDate)
        nextDay.setDate(nextDay.getDate() + 1)
        return updateDate >= customDate && updateDate < nextDay
      }
      default:
        return true
    }
  }

  // Search accepts a list, not just one term — dispatchers paste a column of
  // container numbers straight out of a spreadsheet or an email. Split on
  // whitespace/comma/semicolon and treat the pieces as OR: a row shows if it
  // matches any one of them.
  const searchTokens = useMemo(
    () =>
      (searchTerm || '')
        .split(/[\s,;]+/)
        .map(t => t.trim().toLowerCase())
        .filter(Boolean),
    [searchTerm]
  )

  // Filtered data
  const filteredData = useMemo(() => {
    return data.filter((item) => {
      // 搜索要能命中「屏幕上看得见的字」。Route 那一列画的是短名
      // (warehouseShortName / originShortName / …)，而这里以前只比对仓库全名，
      // 于是全名写错一个字母的仓库（"Amzon-TCY2" 显示成 "Amazon-TCY2"）就永远
      // 搜不到 —— 用户看到 Amazon，搜 Amazon 却没有结果。短名和长名一起进来。
      // 订舱号：出口单在提柜之前没有柜号，那段时间订舱号是唯一认得出它的号，
      // 板上也是这么画的（柜号下面那行 BKG）。屏幕上看得见就得搜得到。
      const searchFields = [
        item.containerNumber, item.bookingNumber, item.taskId, item.shippingLine,
        item.shipper, item.orderingPartyName, item.driver,
        item.warehouse, item.warehouseName, item.warehouseShortName,
        item.originName, item.originShortName,
        item.destinationName, item.destinationShortName,
        item.portName, item.portShortName,
        item.yardName, item.yardShortName,
      ]
      const matchesSearch = searchTokens.length === 0 ||
        searchTokens.some(token => searchFields.some(field => field?.toLowerCase().includes(token)))

      const matchesDriver = !selectedDriver || selectedDriver === "all" || item.driver === selectedDriver
      const matchesShipper = !selectedShipper || selectedShipper === "all" || item.shipper === selectedShipper
      const matchesOrderingParty = !selectedOrderingParty || selectedOrderingParty === "all" ||
        item.orderingPartyName === selectedOrderingParty
      // Default ("" or "all") excludes completed/cancelled — user must click those filters explicitly
      const completedStatuses = ['completed', 'cancelled', 'uncompleted']
      const matchesStatus = selectedStatus
        ? selectedStatus === "all"
          ? !completedStatuses.includes(item.status)
          : item.status === selectedStatus
        : !completedStatuses.includes(item.status)
      const matchesLFD = matchesLFDFilter(item.lfd, lfdFilter || "all", customLfdDate || "")
      const matchesImportExport = !importExportFilter || importExportFilter === "all" || item.importExport === importExportFilter
      // Warehouse dropdown carries the warehouse row's id, so this matches the
      // actual warehouse object the task points at rather than a name string.
      const matchesDestination = !destinationFilter || destinationFilter === "all" ||
        (item.warehouseId != null && String(item.warehouseId) === destinationFilter)
      // The Port dropdown carries the port row's id, so this matches the actual
      // port object the task points at — names/short names vary per task snapshot.
      const matchesPort = !portFilter || portFilter === "all" ||
        (item.portId != null && String(item.portId) === portFilter)
      const matchesMarked = !showMarkedOnly || (markedTaskIds && markedTaskIds.has(item.id))
      const matchesCompletedTime = matchesCompletedTimeFilter(item)

      return matchesSearch && matchesDriver && matchesShipper && matchesOrderingParty &&
             matchesStatus && matchesLFD && matchesImportExport && matchesDestination &&
             matchesPort && matchesMarked && matchesCompletedTime
    })
  }, [
    data, searchTokens, selectedDriver, selectedShipper, selectedOrderingParty, selectedStatus,
    lfdFilter, customLfdDate, importExportFilter, destinationFilter, portFilter,
    showMarkedOnly, markedTaskIds, completedTimeFilter, customCompletedDate
  ])

  // Shared sort comparator
  const compareTasks = (a: ContainerTask, b: ContainerTask, field: SortField, direction: SortDirection): number => {
    let aValue: string | number
    let bValue: string | number

    switch (field) {
      case "taskId":       aValue = a.taskId; bValue = b.taskId; break
      case "status":       aValue = a.status; bValue = b.status; break
      case "driver":       aValue = a.driver; bValue = b.driver; break
      case "taskType":     aValue = a.taskType; bValue = b.taskType; break
      case "portAppointment":
        aValue = a.portAppointment === "Pending" ? "9999-12-31" : a.portAppointment
        bValue = b.portAppointment === "Pending" ? "9999-12-31" : b.portAppointment
        break
      case "lfd":
        aValue = a.lfd === "Pending" ? "9999-12-31" : a.lfd
        bValue = b.lfd === "Pending" ? "9999-12-31" : b.lfd
        break
      default: return 0
    }

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      aValue = aValue.toLowerCase()
      bValue = bValue.toLowerCase()
    }

    if (aValue < bValue) return direction === "asc" ? -1 : 1
    if (aValue > bValue) return direction === "asc" ? 1 : -1
    return 0
  }

  // Ensure paired tasks are always adjacent after sorting
  const ensurePairedAdjacent = (tasks: ContainerTask[]): ContainerTask[] => {
    const result = [...tasks]
    const visited = new Set<string>()
    for (let i = 0; i < result.length; i++) {
      const task = result[i]
      if (visited.has(task.id)) continue
      if (!task.pairedTaskId) continue
      visited.add(task.id)
      const pairedId = String(task.pairedTaskId)
      // Check if next item is already the pair
      if (i + 1 < result.length && result[i + 1].id === pairedId) {
        visited.add(pairedId)
        continue
      }
      // Find and move pair to i+1
      const pairIdx = result.findIndex((t, j) => j > i && t.id === pairedId)
      if (pairIdx > -1) {
        const [paired] = result.splice(pairIdx, 1)
        result.splice(i + 1, 0, paired)
        visited.add(pairedId)
      }
    }
    return result
  }

  // Sorted data
  const sortedData = useMemo(() => {
    const offContainers = filteredData.filter(item => item.status === "off")
    const standbyContainers = filteredData.filter(item => item.status === "standby")
    const regularContainers = filteredData.filter(item => item.status !== "off" && item.status !== "standby")

    let sorted: ContainerTask[]
    // 用户自己点了表头就听他的，永远优先。没点表头时，几个筛选各有各的默认排法 ——
    // 都是那一列状态下面那条日期标签在排，所以看到的顺序和看到的日期永远对得上。
    // 其它视图没有日期可排，保持接口给的顺序不动。
    const sorter = sortField && sortDirection
      ? (a: ContainerTask, b: ContainerTask) => compareTasks(a, b, sortField, sortDirection)
      : defaultSorterFor(selectedStatus)

    if (sorter) {
      sorted = [...regularContainers].sort(sorter).concat(
        [...offContainers].sort(sorter),
        [...standbyContainers].sort(sorter)
      )
    } else {
      sorted = [...regularContainers, ...offContainers, ...standbyContainers]
    }

    // 查验分组放在最后一步，配对相邻在每组里各跑一次 —— 反过来的话，先配好的
    // 一对会被分组从中间劈开，而配对本来就是为了让两条挨在一起看。
    return groupOnHoldSections(sorted, selectedStatus === 'on-hold', ensurePairedAdjacent)
  }, [filteredData, sortField, sortDirection, selectedStatus])

  return {
    filteredData,
    sortedData,
    statusConfig,
    taskTypeConfig,
    getLFDUrgency,
    getLFDStyles,
    formatDateTime,
  }
}
